/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

export interface BridgeFetchOptions {
    credentials?: 'omit' | 'include';
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD';
    headers?: Record<string, string>;
    body?: string;
    timeoutMs?: number;
    responseType?: 'text' | 'json' | 'blob' | 'base64';
}

export interface BridgeFetchResponse {
    ok: boolean;
    status: number;
    statusText: string;
    headers: Record<string, string>;
    data: any;
    error?: string;
    code?: string;
    transport?: import('../network/types.ts').TransportMode;
}

export interface BridgeStatus {
    connected: boolean;
    version?: string;
    origins: string[];
    latencyMs?: number;
    error?: string;
}

const BRIDGE_SOURCE_WEB = 'yomiri-web';
const BRIDGE_SOURCE_EXT = 'yomiri-extension';

/**
 * Client service to communicate with the Yomiri Companion Browser Extension.
 * Dispatches requests through window.postMessage relay (zero configuration required)
 * or direct chrome.runtime.sendMessage when available.
 */
export class ExtensionBridgeClient {
    private static instance: ExtensionBridgeClient;

    private pendingRequests = new Map<
        string,
        {
            resolve: (val: any) => void;
            reject: (err: any) => void;
            timer: any;
        }
    >();

    private isRelayListening = false;

    public static getInstance(): ExtensionBridgeClient {
        if (!ExtensionBridgeClient.instance) {
            ExtensionBridgeClient.instance = new ExtensionBridgeClient();
        }
        return ExtensionBridgeClient.instance;
    }

    private constructor() {
        if (typeof window !== 'undefined') {
            this.initRelayListener();
        }
    }

    private initRelayListener() {
        if (this.isRelayListening) return;
        this.isRelayListening = true;

        window.addEventListener('message', (event) => {
            if (event.source !== window || !event.data || event.data.source !== BRIDGE_SOURCE_EXT) {
                return;
            }

            const { requestId, response } = event.data;
            if (!requestId) return;

            const pending = this.pendingRequests.get(requestId);
            if (pending) {
                clearTimeout(pending.timer);
                this.pendingRequests.delete(requestId);
                pending.resolve(response);
            }
        });
    }

    private sendRelayMessage<T = any>(action: string, payload?: any, timeoutMs: number = 25000): Promise<T> {
        return new Promise((resolve, reject) => {
            const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

            const timer = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                reject(
                    new Error(
                        `Bridge request "${action}" timed out. Ensure the Yomiri Companion extension is installed and loaded.`
                    )
                );
            }, timeoutMs);

            this.pendingRequests.set(requestId, { resolve, reject, timer });

            window.postMessage(
                {
                    source: BRIDGE_SOURCE_WEB,
                    requestId,
                    action,
                    payload,
                },
                '*'
            );
        });
    }

    /**
     * Pings the companion extension to verify connection and measure roundtrip latency.
     */
    public async ping(): Promise<BridgeStatus> {
        const start = performance.now();
        try {
            const res = await this.sendRelayMessage('PING', {}, 4000);
            const latencyMs = Math.round(performance.now() - start);

            if (res && res.ok) {
                return {
                    connected: true,
                    version: res.version,
                    origins: res.origins || [],
                    latencyMs,
                };
            }

            return {
                connected: false,
                origins: [],
                error: res?.error || 'Bridge returned error',
            };
        } catch (err: any) {
            return {
                connected: false,
                origins: [],
                error: err.message || 'Extension bridge not detected',
            };
        }
    }

    /**
     * Checks if the extension has host permission for a specific origin / domain
     */
    public async checkPermission(origin: string): Promise<boolean> {
        try {
            const res = await this.sendRelayMessage('CHECK_PERMISSION', { origin }, 4000);
            return !!res?.granted;
        } catch {
            return false;
        }
    }
    public async openSource(url: string): Promise<void> {
        const response = await this.sendRelayMessage('OPEN_SOURCE', { url }, 6000);
        if (!response?.ok) throw new Error(response?.error || 'No se pudo abrir el sitio. Recargá la extensión Yomiri Companion 1.1.0.');
    }
    public async readSourceDocument(url: string): Promise<string> {
        const response = await this.sendRelayMessage('FETCH_DOCUMENT', { url }, 25000);
        if (!response?.ok || typeof response.data !== 'string') throw new Error(response?.code === 'UNKNOWN_ACTION' ? 'Recargá Yomiri Companion 1.1.0 para usar la conexión asistida' : response?.error || 'No se pudo leer la ventana de la fuente');
        return response.data;
    }

    /**
     * Prompts the user to grant permissions for a list of origins
     */
    public async requestPermission(origins: string[]): Promise<boolean> {
        try {
            const res = await this.sendRelayMessage('REQUEST_PERMISSION', { origins }, 15000);
            return !!res?.granted;
        } catch {
            return false;
        }
    }

    /**
     * Revokes permissions for a list of origins
     */
    public async revokePermission(origins: string[]): Promise<boolean> {
        try {
            const res = await this.sendRelayMessage('REVOKE_PERMISSION', { origins }, 6000);
            return !!res?.removed;
        } catch {
            return false;
        }
    }

    /**
     * Performs a cross-origin HTTP fetch request through the companion extension
     */
    public async fetch(url: string, options: BridgeFetchOptions = {}): Promise<BridgeFetchResponse> {
        if (typeof document !== 'undefined' && document.documentElement.dataset.yomiriBridgeInstalled !== 'true') {
            const error = new Error('La extensión Yomiri no está conectada a esta pestaña. Recargá la página después de activarla.');
            (error as any).code = 'EXTENSION_DISCONNECTED';
            throw error;
        }
        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                const res = await this.sendRelayMessage(
                    'FETCH',
                    { url, options },
                    (options.timeoutMs || 25000) + 2000
                );

                if (!res.ok) {
                    const error = new Error(res.error || 'Fetch failed through companion extension');
                    (error as any).code = res.code;
                    (error as any).status = res.status;
                    throw error;
                }

                return res;
            } catch (err: any) {
                if (!err.code && err.message?.includes('timed out')) err.code = 'TIMEOUT';
                if (attempt === 0 && ['EXTENSION_ERROR', 'NO_RESPONSE', 'TIMEOUT'].includes(err.code)) {
                    await new Promise((resolve) => setTimeout(resolve, 300));
                    continue;
                }
                throw err;
            }
        }
        throw new Error('La extensión no respondió');
    }

    /**
     * Helper to fetch JSON through the extension bridge
     */
    public async fetchJson<T = any>(url: string, options: BridgeFetchOptions = {}): Promise<T> {
        const res = await this.fetch(url, { ...options, responseType: 'json' });
        return res.data;
    }

    /**
     * Helper to fetch an image as a base64 / blob URL through the extension bridge
     */
    public async fetchBlobUrl(url: string, options: BridgeFetchOptions = {}): Promise<string> {
        const res = await this.fetch(url, { ...options, responseType: 'base64' });
        return res.data;
    }
}

export const extensionBridgeClient = ExtensionBridgeClient.getInstance();
export const extensionBridge = extensionBridgeClient;
